import { useState } from 'react'
import { Banner, ErrorBanner } from '@shop/ui'
import { ScanInput, Sheet, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { CustomerSummary } from '../pos/contracts'

/**
 * العميل — <b>اختياريٌّ دائمًا</b>.
 *
 * ومن يشتري شاحنًا بدينارين لا يُسجَّل؛ ومن يشتري هاتفًا بضمانٍ يُسجَّل.
 * وإجبارُ الأوّل يُنتج صفوفًا فارغةً ويُبطّئ الصندوق.
 *
 * ⚠ <b>والبحثُ بالهاتف لا بالاسم</b> — وهو قرارُ المالك: الهاتفُ هو هويّةُ
 *   الزبون، والاسمُ يتكرّر. والخادمُ يوحّد سبعَ صورٍ للرقم، فما يكتبه الكاشيرُ
 *   بأيّ صورةٍ يجد صاحبَه.
 */
export default function CustomerPicker({
  onPick,
  onClose,
}: {
  onPick: (customer: CustomerSummary | null) => void
  onClose: () => void
}) {
  const [phone, setPhone] = useState('')
  const [found, setFound] = useState<CustomerSummary | null>(null)
  const [searched, setSearched] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const search = async () => {
    if (phone.trim() === '') {
      return
    }

    setBusy(true)
    setError(null)
    setSearched(false)

    try {
      const hit = await pos.findCustomerByPhone(phone.trim())

      setFound(hit)
      setSearched(true)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const create = async () => {
    setBusy(true)
    setError(null)

    try {
      const created = await pos.createCustomer(name.trim(), phone.trim())

      onPick(created.customer)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet title="العميل" onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-3">
        <ScanInput
          value={phone}
          onChange={setPhone}
          onSubmit={() => void search()}
          placeholder="رقمُ الهاتف…"
        />

        <TouchButton wide disabled={busy || phone.trim() === ''} onClick={() => void search()}>
          {busy ? 'يُبحث…' : 'ابحث'}
        </TouchButton>

        <ErrorBanner error={error} />

        {found && (
          <div className="rounded-xl border border-ok/40 bg-ok/10 p-3">
            <p className="font-bold">{found.nameAr}</p>
            <p className="tabular text-xs text-ink-soft" dir="ltr">
              {found.phone} · {found.code}
            </p>

            <TouchButton tone="primary" wide onClick={() => onPick(found)}>
              اختر هذا الزبون
            </TouchButton>
          </div>
        )}

        {searched && !found && (
          <div className="grid gap-2">
            <Banner tone="info">لا زبونَ بهذا الرقم. سجّله الآن أو أكمِل بلا عميل.</Banner>

            <label className="block">
              <span className="mb-1 block text-xs text-ink-soft">الاسم</span>
              <input
                className="min-h-12 w-full rounded-lg border border-line bg-surface px-3"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>

            <TouchButton
              tone="primary"
              wide
              disabled={busy || name.trim() === ''}
              onClick={() => void create()}
            >
              سجّل واختر
            </TouchButton>
          </div>
        )}

        <TouchButton wide onClick={() => onPick(null)}>
          أكمِل بلا عميل
        </TouchButton>
      </div>
    </Sheet>
  )
}
